//Tamil-Katral-Maiyam\src\pages\admin\SectionsPage.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { Eye, UserPlus, Pencil } from 'lucide-react';
import {
  listSections,
  createSection,
  getSection,
  patchSection,
  RosterSection,
  CreateSectionPayload,
  listClasses,
  RosterClass,
  listTeachers,
  assignTeacherToSection,
  RosterTeacher,
} from '../../modules/roster/api/roster.client';

export const SectionsPage: React.FC = () => {
  const [sections, setSections] = useState<RosterSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailSection, setDetailSection] = useState<RosterSection | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [classes, setClasses] = useState<RosterClass[]>([]);
  const [teachers, setTeachers] = useState<RosterTeacher[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignSectionId, setAssignSectionId] = useState<string | null>(null);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Edit section modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editSectionId, setEditSectionId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCapacity, setEditCapacity] = useState('');
  const [isPatching, setIsPatching] = useState(false);

  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClassId, setFilterClassId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');

  const createEmptyFormValues = (): CreateSectionPayload => ({
    class_id: '',
    name: '',
    display_name: '',
    capacity: undefined,
    academic_year_key: '',
  });

  const [formValues, setFormValues] = useState<CreateSectionPayload>(createEmptyFormValues());

  // ✅ Placeholders for ALL fields
  const placeholders: Record<string, string> = {
    class_id: 'Select a class',
    name: 'Enter section name (e.g., A / B)',
    display_name: 'Auto-generated (Class + Section)',
    capacity: 'Enter capacity (e.g., 30)',
    academic_year_key: 'Auto-filled from selected class (e.g., 2025-2026)',
    teacher: 'Select teacher',
  };

  const loadSections = async () => {
    setLoading(true);
    try {
      const items = await listSections({ limit: 50, skip: 0 });
      setSections(items);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    try {
      const classItems = await listClasses({ limit: 50, skip: 0 });
      setClasses(classItems);
    } catch (error) {
      showErrorToast(error);
    }
  };

  const loadTeachers = async () => {
    if (teachers.length > 0 && !teachersLoading) {
      return;
    }
    setTeachersLoading(true);
    try {
      const teacherItems = await listTeachers({ limit: 200, skip: 0 });
      setTeachers(teacherItems);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setTeachersLoading(false);
    }
  };

  useEffect(() => {
    loadSections();
    loadClasses();
  }, []);

  useEffect(() => {
    if (!isAddModalOpen) return;
    if (classes.length === 1 && !formValues.class_id) {
      const cls = classes[0];
      setFormValues((prev) => ({
        ...prev,
        class_id: cls.id,
        academic_year_key: cls.academic_year_key ?? '',
        display_name: formatDisplayName(cls.name, prev.name),
      }));
      return;
    }
    if (!formValues.class_id) return;
    const selectedClass = classes.find((c) => c.id === formValues.class_id);
    const academicYear = selectedClass?.academic_year_key ?? '';
    if (academicYear !== formValues.academic_year_key) {
      setFormValues((prev) => ({ ...prev, academic_year_key: academicYear }));
    }
  }, [isAddModalOpen, formValues.class_id, formValues.academic_year_key, formValues.name, classes]);

  const resetFormValues = () => setFormValues(createEmptyFormValues());

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    resetFormValues();
  };

  const formatDisplayName = (className: string | undefined, sectionName: string) => {
    const trimmedSection = sectionName.trim();
    if (!trimmedSection) return className ?? '';
    if (!className) return trimmedSection;
    return `${className} - ${trimmedSection}`;
  };

  const handleClassChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = event.target.value;
    const selectedClass = classes.find((cls) => cls.id === selectedId);
    setFormValues((prev) => ({
      ...prev,
      class_id: selectedId,
      academic_year_key: selectedClass?.academic_year_key ?? '',
      display_name: formatDisplayName(selectedClass?.name, prev.name),
    }));
  };

  const handleSectionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newName = event.target.value;
    const selectedClass = classes.find((cls) => cls.id === formValues.class_id);
    setFormValues((prev) => ({
      ...prev,
      name: newName,
      display_name: formatDisplayName(selectedClass?.name, newName),
    }));
  };

  const openDetail = async (sectionId: string) => {
    setDetailLoading(true);
    try {
      const data = await getSection(sectionId);
      setDetailSection(data);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const openEditModal = (section: RosterSection) => {
    setEditSectionId(section.id);
    setEditName(section.name ?? '');
    setEditCapacity(section.capacity != null ? String(section.capacity) : '');
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editSectionId || !editName.trim()) return;
    setIsPatching(true);
    try {
      const payload: { name: string; capacity?: number } = { name: editName.trim() };
      const cap = editCapacity.trim() ? Number(editCapacity) : undefined;
      if (cap != null && !Number.isNaN(cap)) payload.capacity = cap;
      await patchSection(editSectionId, payload);
      showSuccessToast('Section updated.');
      setIsEditModalOpen(false);
      setEditSectionId(null);
      setEditName('');
      setEditCapacity('');
      await loadSections();
      if (detailSection?.id === editSectionId) {
        const data = await getSection(editSectionId);
        setDetailSection(data);
      }
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsPatching(false);
    }
  };

  const openAssignModal = (sectionId: string) => {
    setAssignSectionId(sectionId);
    setSelectedTeacherId('');
    setIsAssignModalOpen(true);
    loadTeachers();
  };

  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setAssignSectionId(null);
    setSelectedTeacherId('');
  };

  const handleAddSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formValues.class_id) {
      showErrorToast('Please select a class before creating a section');
      return;
    }
    if (!formValues.academic_year_key) {
      showErrorToast('Academic year is required');
      return;
    }
    setIsCreating(true);
    try {
      await createSection({
        ...formValues,
        capacity: formValues.capacity,
      });
      showSuccessToast('Section created');
      closeAddModal();
      await loadSections();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleAssignSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!assignSectionId) {
      showErrorToast('Section context missing for assignment');
      return;
    }
    if (!selectedTeacherId) {
      showErrorToast('Please select a teacher before assigning');
      return;
    }
    setIsAssigning(true);
    try {
      await assignTeacherToSection(assignSectionId, selectedTeacherId);
      showSuccessToast('Teacher assigned');
      closeAssignModal();
      await loadSections();
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsAssigning(false);
    }
  };

  const classById = useMemo(
    () => new Map(classes.map((cls) => [cls.id, cls])),
    [classes]
  );

  const getDisplayLabel = (section: RosterSection) => {
    const cls = classById.get(section.class_id);
    return section.display_name || formatDisplayName(cls?.name, section.name);
  };

  const getClassGradeLabel = (section: RosterSection) => {
    const cls = classById.get(section.class_id);
    return cls?.grade || cls?.name || section.class_id || '-';
  };

  const formatTeacherLabel = (teacher: RosterTeacher) => {
    const fallbackName = `${teacher.first_name || ''} ${teacher.last_name || ''}`.trim();
    return teacher.display_name || fallbackName || teacher.email || teacher.id;
  };

  const teacherOptions = [
    { label: placeholders.teacher, value: '' },
    ...teachers.map((teacher) => ({
      label: formatTeacherLabel(teacher),
      value: teacher.id,
    })),
  ];

  // Filter options from loaded data
  const filterClassOptions = useMemo(
    () => [
      { label: 'All classes', value: '' },
      ...classes.map((c) => ({ label: c.name ?? c.id, value: c.id })),
    ],
    [classes]
  );
  const filterStatusOptions = useMemo(
    () => [
      { label: 'All statuses', value: '' },
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
    ],
    []
  );

  const filteredSections = useMemo(() => {
    let list = sections;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const cls = classById.get(s.class_id);
        const display = getDisplayLabel(s);
        return (
          (s.name?.toLowerCase().includes(q) ?? false) ||
          (s.academic_year_key?.toLowerCase().includes(q) ?? false) ||
          (display?.toLowerCase().includes(q) ?? false) ||
          (cls?.name?.toLowerCase().includes(q) ?? false)
        );
      });
    }
    if (filterClassId) list = list.filter((s) => s.class_id === filterClassId);
    if (filterStatus) list = list.filter((s) => s.status === filterStatus);
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      from.setHours(0, 0, 0, 0);
      list = list.filter((s) => s.created_at && new Date(s.created_at) >= from);
    }
    return list;
  }, [sections, searchQuery, filterClassId, filterStatus, filterDateFrom, classById]);

  const hasActiveFilters = Boolean(searchQuery.trim() || filterClassId || filterStatus || filterDateFrom);
  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterClassId('');
    setFilterStatus('');
    setFilterDateFrom('');
  };

  // Detail view: only Name, Capacity, Academic Year, Status (inline "field: value")
  const detailFields = useMemo(
    () => [
      { label: 'Name', value: detailSection?.name },
      { label: 'Capacity', value: detailSection?.capacity?.toString() },
      { label: 'Academic Year', value: detailSection?.academic_year_key },
      { label: 'Status', value: detailSection?.status },
    ],
    [detailSection]
  );

  return (
    <Layout>
      <PageHeader
        title="Sections"
        subtitle="Manage school sections"
        actions={<Button onClick={() => setIsAddModalOpen(true)}>Add Section</Button>}
      />

      <Card>
        <CardContent padding="md">
          {/* Search and filters */}
          <div className="mb-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-full min-w-0 sm:w-64 flex-1 sm:flex-initial">
                <Input
                  placeholder="Search name, class, year..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="w-full min-w-0 sm:w-40">
                <Select
                  options={filterClassOptions}
                  value={filterClassId}
                  onChange={(e) => setFilterClassId(e.target.value)}
                />
              </div>
              <div className="w-full min-w-0 sm:w-36">
                <Select
                  options={filterStatusOptions}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                />
              </div>
              <div className="w-full min-w-0 sm:w-40">
                <Input
                  type="date"
                  // label="Created from"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                />
              </div>
              {hasActiveFilters && (
                <Button type="button" variant="outline" size="sm" onClick={clearAllFilters} className="shrink-0">
                  Clear
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
            <Table className="min-w-[600px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Display</TableHead>
                  <TableHead>Class Grade</TableHead>
                  <TableHead>Section Name</TableHead>
                  <TableHead>Academic Year</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSections.map((section) => (
                  <TableRow key={section.id} onClick={() => openDetail(section.id)}>
                    <TableCell>{getDisplayLabel(section)}</TableCell>
                    <TableCell>{getClassGradeLabel(section)}</TableCell>
                    <TableCell>{section.name}</TableCell>
                    <TableCell>{section.academic_year_key || '-'}</TableCell>
                    <TableCell>{section.status}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1.5"
                          onClick={(event) => {
                            event.stopPropagation();
                            openDetail(section.id);
                          }}
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1.5"
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditModal(section);
                          }}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1.5"
                          onClick={(event) => {
                            event.stopPropagation();
                            openAssignModal(section.id);
                          }}
                          title="Assign Teacher"
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={isAddModalOpen} onClose={closeAddModal} title="Add Section">
        <form className="space-y-4" onSubmit={handleAddSubmit}>
          <Select
            label="Class"
            options={[
              { label: placeholders.class_id, value: '' },
              ...classes.map((cls) => ({ label: cls.name ?? cls.id, value: cls.id })),
            ]}
            value={formValues.class_id}
            onChange={handleClassChange}
            error={!formValues.class_id ? 'Class is required' : undefined}
            helperText={!formValues.class_id ? placeholders.class_id : undefined}
          />

          <Input
            label="Section Name"
            required
            placeholder={placeholders.name}
            value={formValues.name}
            onChange={handleSectionNameChange}
          />

          <Input
            label="Display Name"
            value={formValues.display_name}
            readOnly
            placeholder={placeholders.display_name}
          />

          <Input
            label="Capacity"
            type="number"
            placeholder={placeholders.capacity}
            value={formValues.capacity?.toString() || ''}
            onChange={(event) =>
              setFormValues((prev) => ({
                ...prev,
                capacity: event.target.value ? Number(event.target.value) : undefined,
              }))
            }
          />

          <Input
            label="Academic Year"
            value={formValues.academic_year_key}
            readOnly
            placeholder={placeholders.academic_year_key}
          />

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={closeAddModal} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isCreating}>
              Create
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isAssignModalOpen}
        onClose={closeAssignModal}
        title="Assign Teacher"
        size="sm"
      >
        <form className="space-y-4" onSubmit={handleAssignSubmit}>
          <Select
            label="Teacher"
            options={teacherOptions}
            value={selectedTeacherId}
            onChange={(event) => setSelectedTeacherId(event.target.value)}
            error={!selectedTeacherId ? 'Teacher is required' : undefined}
            helperText={
              teachersLoading ? 'Loading teachers...' : !selectedTeacherId ? placeholders.teacher : undefined
            }
            disabled={teachersLoading}
          />

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={closeAssignModal} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isAssigning}>
              Assign
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditSectionId(null);
          setEditName('');
          setEditCapacity('');
        }}
        title="Edit Section"
        size="sm"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <Input
            label="Name"
            required
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="Section name"
          />
          <Input
            label="Capacity"
            type="number"
            value={editCapacity}
            onChange={(e) => setEditCapacity(e.target.value)}
            placeholder="e.g. 60"
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditSectionId(null);
                setEditName('');
                setEditCapacity('');
              }}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={isPatching}>
              Save
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(detailSection)}
        onClose={() => setDetailSection(null)}
        title={detailSection ? detailSection.display_name || detailSection.name : 'Section details'}
      >
        {detailLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {detailFields.map((row) => (
              <p key={row.label} className="text-gray-700 dark:text-gray-300">
                <span className="font-medium text-gray-600 dark:text-gray-400">{row.label}:</span>{' '}
                <span className="text-gray-900 dark:text-white">{row.value ?? '-'}</span>
              </p>
            ))}
          </div>
        )}
      </Modal>
    </Layout>
  );
};
